import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { AlertTriangle, Upload, Trash2, RefreshCw, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Broken Images Repair Tool.
 * - Lists slides whose image_url points to a file that has vanished from disk
 *   (typical after a production redeploy that wipes ephemeral storage).
 * - Batch re-upload: pick many files; backend auto-matches each to a broken slide
 *   by the *original* filename embedded in the old URL.
 * - Per-slide re-upload: pick a single file and reattach it directly.
 * - Clear all: drops image_url on every broken slide so they render text-only.
 *
 * After any change the parent reloads the journal via `onChanged()`.
 */
export default function BrokenImagesDialog({ open, onOpenChange, journalId, onChanged }) {
  const [broken, setBroken] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const batchInputRef = useRef(null);

  const fetchBroken = async () => {
    if (!journalId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/journals/${journalId}/broken-images`);
      setBroken(data.broken || []);
      setTotal(data.total_slides || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to scan for broken images");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchBroken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, journalId]);

  const onBatchReupload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/journals/${journalId}/broken-images/batch-reupload`, fd);
      const matched = data.matched || 0;
      const unmatched = (data.total_files || 0) - matched;
      if (matched > 0) {
        toast.success(`Re-attached ${matched} image${matched === 1 ? "" : "s"}`);
      }
      if (unmatched > 0) {
        toast.message(`${unmatched} file${unmatched === 1 ? "" : "s"} uploaded but didn't match a broken slide by filename.`);
      }
      onChanged?.();
      await fetchBroken();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Batch re-upload failed");
    } finally {
      setBusy(false);
      if (batchInputRef.current) batchInputRef.current.value = "";
    }
  };

  const onSlideReupload = async (slideId, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/journals/${journalId}/broken-images/reattach/${slideId}`, fd);
      toast.success("Image re-attached");
      onChanged?.();
      await fetchBroken();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onClearAll = async () => {
    if (!broken.length) return;
    if (!window.confirm(`Clear the broken logo on ${broken.length} slide${broken.length === 1 ? "" : "s"}? Text-only rendering will be used.`)) return;
    setBusy(true);
    try {
      await api.post(`/journals/${journalId}/broken-images/clear`, {
        slide_ids: broken.map((b) => b.slide_id),
      });
      toast.success("Cleared");
      onChanged?.();
      await fetchBroken();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Clear failed");
    } finally {
      setBusy(false);
    }
  };

  const onClearOne = async (slideId) => {
    setBusy(true);
    try {
      await api.post(`/journals/${journalId}/broken-images/clear`, { slide_ids: [slideId] });
      onChanged?.();
      await fetchBroken();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Clear failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="broken-images-dialog"
        className="max-w-2xl bg-zinc-950 border-zinc-800 text-zinc-100"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            Broken images repair
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Logos referenced by these slides can&apos;t be found on the server (commonly after
            a redeploy that wiped uploads). Re-upload them in bulk — files are matched to
            slides by their original filename.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-zinc-400" data-testid="broken-summary">
              {loading
                ? "Scanning…"
                : broken.length === 0
                  ? `No broken images detected (${total} slide${total === 1 ? "" : "s"} scanned).`
                  : `${broken.length} broken / ${total} total slides`}
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="rescan-broken-btn"
                variant="outline"
                size="sm"
                onClick={fetchBroken}
                disabled={loading || busy}
                className="border-zinc-800 hover:bg-zinc-900 text-zinc-100"
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />
                Rescan
              </Button>
            </div>
          </div>

          {broken.length > 0 && (
            <>
              <div className="flex items-center gap-2 border border-amber-500/30 bg-amber-500/5 rounded-md p-3">
                <Upload className="w-4 h-4 text-amber-300" />
                <div className="flex-1 text-xs text-amber-100">
                  Pick the original logo files — we&apos;ll match each to its slide by filename.
                </div>
                <input
                  ref={batchInputRef}
                  data-testid="broken-batch-input"
                  type="file"
                  multiple
                  accept="image/*,application/pdf,.svg,.eps,.ai,.heic,.heif"
                  onChange={onBatchReupload}
                  disabled={busy}
                  className="text-xs file:mr-2 file:px-3 file:py-1 file:rounded file:border-0 file:bg-amber-500 file:text-amber-950 file:cursor-pointer file:hover:bg-amber-400"
                />
              </div>

              <div className="max-h-80 overflow-y-auto border border-zinc-800 rounded-md divide-y divide-zinc-800">
                {broken.map((b) => (
                  <div
                    key={b.slide_id}
                    data-testid={`broken-row-${b.slide_id}`}
                    className="px-3 py-2 flex items-center gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-zinc-100 truncate font-medium">
                        {b.company_name || "(untitled slide)"}
                      </div>
                      <div className="text-xs text-zinc-500 font-mono truncate">
                        {b.original_filename || b.image_url}
                      </div>
                    </div>
                    <label
                      className="cursor-pointer text-xs px-2 py-1 rounded border border-zinc-700 hover:bg-zinc-900 inline-flex items-center gap-1"
                      data-testid={`broken-reupload-${b.slide_id}`}
                    >
                      <Upload className="w-3 h-3" />
                      Re-upload
                      <input
                        type="file"
                        accept="image/*,application/pdf,.svg,.eps,.ai,.heic,.heif"
                        className="hidden"
                        disabled={busy}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) onSlideReupload(b.slide_id, f);
                          e.target.value = "";
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      data-testid={`broken-clear-${b.slide_id}`}
                      onClick={() => onClearOne(b.slide_id)}
                      disabled={busy}
                      className="text-xs text-zinc-400 hover:text-red-400 p-1"
                      title="Clear image (text-only)"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {broken.length === 0 && !loading && (
            <div className="border border-zinc-800 rounded-md p-6 text-center text-zinc-400 text-sm">
              <Check className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
              All slide images resolve correctly.
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {broken.length > 0 && (
            <Button
              data-testid="clear-all-broken-btn"
              variant="ghost"
              onClick={onClearAll}
              disabled={busy}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Clear all ({broken.length})
            </Button>
          )}
          <Button
            data-testid="broken-dialog-close"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-zinc-800 hover:bg-zinc-900 text-zinc-100"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
